import React, { useState, useEffect } from 'react';
import { useAuthProvider } from '../../providers/AuthProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus } from '@fortawesome/free-solid-svg-icons';
import { getKpiCustomers, generateKpis, saveKpis, getSavedKpis, getSavedKpiPeriods, updateKpis } from '../../api/kpis';

// ─── Module-level constants & helpers ────────────────────────────────────────
// These MUST stay outside KpiTab so React doesn't treat them as new component
// types on every render (which would kill input focus on each keystroke).

const STATUS_CONFIG = {
    on_time: { label: 'On Time',  color: '#28a745' },
    late:    { label: 'Late',     color: '#dc3545' },
    no_pod:  { label: 'No POD',   color: '#fd7e14' },
    return:  { label: 'Return',   color: '#6c757d' },
    exempt:  { label: 'Exempt',   color: '#17a2b8' },
};

const fmtDate = (v) => v ? v.slice(0, 10).split('-').reverse().join('/') : null;

const Dash = () => <FontAwesomeIcon icon={faMinus} style={{ color: '#bbb', fontSize: '10px', opacity: 0.7, width:'10px' }} />;

// "01-06-2026_30-06-2026" → "01/06/2026 – 30/06/2026"
const fmtPeriod = (p) => {
    if (!p) return null;
    const [s, e] = p.split('_');
    if (!s || !e) return p;
    return `${s.replace(/-/g, '/')} – ${e.replace(/-/g, '/')}`;
};

function summaryStats(rows) {
    const counted = rows.filter(r => !r.npc);
    const npcCount = rows.length - counted.length;
    const total = counted.length;
    const counts = {};
    counted.forEach(r => { counts[r.kpi_status] = (counts[r.kpi_status] || 0) + 1; });
    const pass = (counts.on_time || 0) + (counts.return || 0) + (counts.exempt || 0);
    const pct  = total > 0 ? Math.round((pass / total) * 100) : 0;
    return { total, counts, pct, npcCount, allTotal: rows.length };
}

function StatusBadge({ status }) {
    const cfg = STATUS_CONFIG[status] || { label: status, color: '#999' };
    return (
        <span style={{ background: cfg.color, color: '#fff', borderRadius: 3, padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>
            {cfg.label}
        </span>
    );
}

function SummaryBar({ rows, colors }) {
    const { total, counts, pct, npcCount, allTotal } = summaryStats(rows);
    return (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, padding: '12px 16px', background: colors.cardBg, borderRadius: 8, border: `1px solid ${colors.border}`, alignItems: 'center' }}>
            <div style={{ fontWeight: 700, fontSize: '1.4em', color: pct >= 95 ? '#28a745' : pct >= 80 ? '#fd7e14' : '#dc3545' }}>{pct}%</div>
            <div style={{ color: colors.muted, fontSize: '12px' }}>KPI Score</div>
            <div style={{ borderLeft: `1px solid ${colors.border}`, height: 28 }} />
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) =>
                counts[key] ? (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ background: cfg.color, color: '#fff', borderRadius: 3, padding: '2px 7px', fontSize: '11px', fontWeight: 700 }}>{cfg.label}</span>
                        <span style={{ fontWeight: 700 }}>{counts[key]}</span>
                    </div>
                ) : null
            )}
            {npcCount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ background: '#8b5cf6', color: '#fff', borderRadius: 3, padding: '2px 7px', fontSize: '11px', fontWeight: 700 }}>NPC</span>
                    <span style={{ fontWeight: 700 }}>{npcCount}</span>
                </div>
            )}
            <div style={{ color: colors.muted, fontSize: '12px', marginLeft: 'auto' }}>
                {total} counted{npcCount > 0 ? ` \u00b7 ${allTotal} total` : ''}
            </div>
        </div>
    );
}

function KpiTable({ rows, editable, isDark, colors, onCommentChange, onNpcChange }) {
    const thS = {
        background: colors.headerBg, padding: '8px 10px', textAlign: 'left',
        borderBottom: `2px solid ${colors.border}`, fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
    };
    const tdS = (extra = {}) => ({
        padding: '7px 10px', borderBottom: `1px solid ${colors.border}`,
        fontSize: '13px', verticalAlign: 'middle', ...extra,
    });

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                    <tr>
                        {['NPC', 'Status', 'Consignment', 'Collection', 'Delivery Date', 'POD Date', 'POD Time', 'Deliver To', 'Postcode', 'Pallets', 'Surcharges', 'Your Ref', 'Comment'].map(h => (
                            <th key={h} style={{ ...thS, ...(h === 'NPC' ? { textAlign: 'center' } : {}), ...(h === 'Status' ? { minWidth: 90 } : {}), ...(h === 'Consignment' ? { maxWidth: 120 } : {}) }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => {
                        const rowBg = isDark ? (i % 2 === 0 ? '#222' : '#2a2a2a') : (i % 2 === 0 ? '#fff' : '#f9f9f9');
                        const npcBg = isDark ? '#1e1b2e' : '#f3f0ff';
                        return (
                            <tr key={row.id || i} style={{ background: row.npc ? npcBg : rowBg, opacity: row.npc ? 0.75 : 1 }}>
                                <td style={tdS({ textAlign: 'center', minWidth: 50 })}>
                                    {editable ? (
                                        <input
                                            type="checkbox"
                                            checked={!!row.npc}
                                            onChange={e => onNpcChange(i, e.target.checked)}
                                            title="Non-Palletforce Consignment — excluded from KPI score"
                                            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#8b5cf6' }}
                                        />
                                    ) : (
                                        row.npc ? <span style={{ background: '#8b5cf6', color: '#fff', borderRadius: 3, padding: '2px 6px', fontSize: '10px', fontWeight: 700 }}>NPC</span> : null
                                    )}
                                </td>
                                <td style={tdS()}><StatusBadge status={row.kpi_status} /></td>
                                <td style={tdS({ fontFamily: 'monospace', whiteSpace: 'nowrap', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' })}>{row.consignment_number || <Dash />}</td>
                                <td style={tdS({ whiteSpace: 'nowrap' })}>{fmtDate(row.collection_date) || <Dash />}</td>
                                <td style={tdS({ whiteSpace: 'nowrap' })}>{fmtDate(row.delivery_date) || <Dash />}</td>
                                <td style={tdS({ whiteSpace: 'nowrap', color: row.kpi_status === 'late' ? '#dc3545' : 'inherit', fontWeight: row.kpi_status === 'late' ? 700 : 400 })}>{fmtDate(row.pod_date) || <Dash />}</td>
                                <td style={tdS({ whiteSpace: 'nowrap' })}>{row.pod_time || <Dash />}</td>
                                <td style={tdS()}>{row.delivery_name || <Dash />}</td>
                                <td style={tdS({ whiteSpace: 'nowrap' })}>{row.delivery_postcode || <Dash />}</td>
                                <td style={tdS({ textAlign: 'center' })}>{row.number_of_pallets ?? <Dash />}</td>
                                <td style={tdS({ whiteSpace: 'nowrap', fontSize: '11px', color: colors.muted })}>{row.surcharges || <Dash />}</td>
                                <td style={tdS({ color: colors.muted })}>{row.customer_reference || <Dash />}</td>
                                <td style={tdS({ minWidth: 180 })}>
                                    {editable ? (
                                        <input
                                            type="text"
                                            value={row.admin_comment || ''}
                                            onChange={e => onCommentChange(i, e.target.value)}
                                            placeholder="Add comment..."
                                            style={{
                                                background: colors.inputBg, color: colors.inputFg,
                                                border: `1px solid ${colors.inputBorder}`,
                                                borderRadius: 4, padding: '4px 8px', fontSize: '13px', width: '100%',
                                            }}
                                        />
                                    ) : (
                                        <span style={{ color: colors.muted }}>{row.admin_comment || <Dash />}</span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function KpiTab({ theme = 'light', showAlert, integrationId }) {
    const isDark = theme === 'dark';

    const colors = {
        bg:          isDark ? '#1a1a1a' : '#fff',
        fg:          isDark ? '#e0e0e0' : '#333',
        border:      isDark ? '#444' : '#dee2e6',
        cardBg:      isDark ? '#2d2d2d' : '#f8f9fa',
        headerBg:    isDark ? '#252525' : '#e9ecef',
        muted:       isDark ? '#aaa' : '#666',
        inputBg:     isDark ? '#2a2a2a' : '#fff',
        inputFg:     isDark ? '#eee' : '#222',
        inputBorder: isDark ? '#555' : '#ccc',
    };
    const inputStyle = {
        background: colors.inputBg, color: colors.inputFg,
        border: `1px solid ${colors.inputBorder}`, borderRadius: 4,
        padding: '6px 10px', fontSize: '13px',
    };
    const btn = (variant, extra = {}) => ({
        border: 'none', borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
        background: variant === 'primary' ? '#1997eb' : variant === 'success' ? '#28a745' : variant === 'danger' ? '#dc3545'
                  : variant === 'secondary' ? (isDark ? '#444' : '#e0e0e0') : (isDark ? '#333' : '#f0f0f0'),
        color: ['primary', 'success', 'danger'].includes(variant) ? '#fff' : colors.fg,
        ...extra,
    });

    const { permissions, isDev } = useAuthProvider();
    const canEdit = isDev || (permissions && permissions.perm_palletforce_edit);

    // ── Mode ─────────────────────────────────────────────────────────────────
    const [mode, setMode] = useState(canEdit ? 'generate' : 'saved');

    // ── Customers ────────────────────────────────────────────────────────────
    const [customers, setCustomers]               = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [loadingCustomers, setLoadingCustomers] = useState(false);

    useEffect(() => {
        setLoadingCustomers(true);
        getKpiCustomers()
            .then(r => setCustomers(Array.isArray(r.data) ? r.data : []))
            .catch(() => setCustomers([]))
            .finally(() => setLoadingCustomers(false));
    }, []);

    // ── Generate mode ────────────────────────────────────────────────────────
    const [startDate, setStartDate]   = useState('');
    const [endDate, setEndDate]       = useState('');
    const [generating, setGenerating] = useState(false);
    const [kpiRows, setKpiRows]       = useState(null);
    const [saving, setSaving]         = useState(false);

    // ── Saved mode ───────────────────────────────────────────────────────────
    const [periods, setPeriods]               = useState([]);
    const [loadingPeriods, setLoadingPeriods] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(''); // e.g. "01-06-2026_30-06-2026"
    const [savedRows, setSavedRows]           = useState([]);
    const [loadingSaved, setLoadingSaved]     = useState(false);

    // ── Saved mode: editing ───────────────────────────────────────────────
    const [isEditing, setIsEditing]         = useState(false);
    const [editingRows, setEditingRows]     = useState([]);
    const [savingEdits, setSavingEdits]     = useState(false);

    // Load periods when customer selected & mode=saved
    useEffect(() => {
        if (!selectedCustomer || mode !== 'saved') return;
        setLoadingPeriods(true);
        setSavedRows([]);
        setSelectedPeriod('');
        getSavedKpiPeriods(selectedCustomer.id)
            .then(r => setPeriods(Array.isArray(r.data) ? r.data : []))
            .catch(() => setPeriods([]))
            .finally(() => setLoadingPeriods(false));
    }, [selectedCustomer, mode]);

    // Load rows when period selected
    useEffect(() => {
        if (!selectedCustomer || !selectedPeriod) return;
        setLoadingSaved(true);
        setIsEditing(false);
        getSavedKpis(selectedCustomer.id, selectedPeriod)
            .then(r => setSavedRows(Array.isArray(r.data) ? r.data : []))
            .catch(() => setSavedRows([]))
            .finally(() => setLoadingSaved(false));
    }, [selectedPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleGenerate = async () => {
        if (!selectedCustomer)      return showAlert({ message: 'Please select a customer.', type: 'warning', buttons: [{ label: 'OK' }] });
        if (!startDate || !endDate) return showAlert({ message: 'Please select a date range.', type: 'warning', buttons: [{ label: 'OK' }] });
        if (startDate > endDate)    return showAlert({ message: 'Start date must be before end date.', type: 'warning', buttons: [{ label: 'OK' }] });
        setGenerating(true);
        setKpiRows(null);
        const r = await generateKpis(selectedCustomer.id, integrationId, startDate, endDate);
        setGenerating(false);
        if (!r.ok) {
            showAlert({ message: r.data?.error || 'Failed to generate KPIs.', type: 'danger', buttons: [{ label: 'Close' }] });
            return;
        }
        setKpiRows(Array.isArray(r.data) ? r.data : []);
    };

    const updateComment = (index, value) =>
        setKpiRows(prev => prev.map((row, i) => i === index ? { ...row, admin_comment: value } : row));

    const updateNpc = (index, value) =>
        setKpiRows(prev => prev.map((row, i) => i === index ? { ...row, npc: value } : row));

    const updateEditComment = (index, value) =>
        setEditingRows(prev => prev.map((row, i) => i === index ? { ...row, admin_comment: value } : row));

    const updateEditNpc = (index, value) =>
        setEditingRows(prev => prev.map((row, i) => i === index ? { ...row, npc: value } : row));

    const handleStartEdit = () => {
        setEditingRows(savedRows.map(r => ({ ...r })));
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingRows([]);
    };

    const handleSaveEdits = async () => {
        setSavingEdits(true);
        const updates = editingRows.map(r => ({ id: r.id, admin_comment: r.admin_comment, npc: r.npc }));
        const result = await updateKpis(updates);
        setSavingEdits(false);
        if (!result.ok) {
            showAlert({ message: result.data?.error || 'Failed to save changes.', type: 'danger', buttons: [{ label: 'Close' }] });
            return;
        }
        setSavedRows(editingRows);
        setIsEditing(false);
        setEditingRows([]);
        showAlert({ message: `${result.data?.updated || updates.length} records updated.`, type: 'success', buttons: [{ label: 'OK' }] });
    };

    const handleSave = async () => {
        if (!kpiRows || kpiRows.length === 0) return;
        setSaving(true);
        const r = await saveKpis(kpiRows);
        setSaving(false);
        if (!r.ok) {
            showAlert({ message: r.data?.error || 'Failed to save KPIs.', type: 'danger', buttons: [{ label: 'Close' }] });
            return;
        }
        showAlert({ message: `${r.data?.saved || kpiRows.length} KPI records saved successfully.`, type: 'success', buttons: [{ label: 'OK' }] });
        setKpiRows(null);
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div style={{ color: colors.fg }}>
            {/* ── Controls ─────────────────────────────────────────────────── */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 20, padding: '16px', background: colors.cardBg, borderRadius: 8, border: `1px solid ${colors.border}` }}>

                {/* Customer selector */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: colors.muted, textTransform: 'uppercase' }}>Customer</label>
                    <select
                        value={selectedCustomer?.id || ''}
                        onChange={e => {
                            const cust = customers.find(c => String(c.id) === e.target.value) || null;
                            setSelectedCustomer(cust);
                            setKpiRows(null);
                            setSavedRows([]);
                            setSelectedPeriod('');
                        }}
                        style={{ ...inputStyle, minWidth: 220 }}
                    >
                        <option value="">{loadingCustomers ? 'Loading...' : 'Select customer...'}</option>
                        {customers.map(cust => (
                            <option key={cust.id} value={cust.id}>{cust.company} ({cust.account_ref || 'no ref'})</option>
                        ))}
                    </select>
                </div>

                {/* Mode toggle */}
                {canEdit && (
                    <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: `1px solid ${colors.border}` }}>
                        {[['generate', '+ Generate KPIs'], ['saved', '\ud83d\udccb View Saved']].map(([key, label]) => (
                            <button key={key}
                                style={{ ...btn(mode === key ? 'primary' : 'secondary'), borderRadius: 0, border: 'none' }}
                                onClick={() => { setMode(key); setKpiRows(null); }}>
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                {/* Generate mode: date pickers */}
                {mode === 'generate' && (<>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: colors.muted, textTransform: 'uppercase' }}>From</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={inputStyle} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: colors.muted, textTransform: 'uppercase' }}>To</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={inputStyle} />
                    </div>
                    <button style={btn('primary', { alignSelf: 'flex-end' })} onClick={handleGenerate} disabled={generating}>
                        {generating ? 'Generating\u2026' : 'Generate'}
                    </button>
                </>)}

                {/* Saved mode: period picker */}
                {mode === 'saved' && selectedCustomer && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <label style={{ fontSize: '11px', fontWeight: 700, color: colors.muted, textTransform: 'uppercase' }}>Period</label>
                        <select
                            value={selectedPeriod}
                            onChange={e => setSelectedPeriod(e.target.value)}
                            style={{ ...inputStyle, minWidth: 220 }}
                        >
                            <option value="">{loadingPeriods ? 'Loading...' : 'Select period...'}</option>
                            {periods.map(p => (
                                <option key={p.period} value={p.period}>{fmtPeriod(p.period)}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* ── Generate mode: results ───────────────────────────────────── */}
            {mode === 'generate' && kpiRows !== null && (
                kpiRows.length === 0
                    ? <p style={{ color: colors.muted, textAlign: 'center', padding: 40 }}>No consignments found for this period.</p>
                    : <>
                        <SummaryBar rows={kpiRows} colors={colors} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                            <span style={{ color: colors.muted, fontSize: '13px' }}>{kpiRows.length} consignments <FontAwesomeIcon icon={faMinus} style={{ width: 10, margin: '0 2px', opacity: 0.5 }} /> edit comments below, then save when ready.</span>
                            <button style={btn('success')} onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving\u2026' : `\ud83d\udcbe Save ${kpiRows.length} KPIs`}
                            </button>
                        </div>
                        <KpiTable rows={kpiRows} editable={true} isDark={isDark} colors={colors}
                            onCommentChange={updateComment} onNpcChange={updateNpc} />
                    </>
            )}

            {/* ── Saved mode: results ──────────────────────────────────────── */}
            {mode === 'saved' && (
                <>
                    {!selectedCustomer && <p style={{ color: colors.muted, textAlign: 'center', padding: 40 }}>Select a customer to view saved KPIs.</p>}
                    {selectedCustomer && !selectedPeriod && periods.length === 0 && !loadingPeriods && (
                        <p style={{ color: colors.muted, textAlign: 'center', padding: 40 }}>No saved KPI periods found for this customer.</p>
                    )}
                    {loadingSaved && <p style={{ color: colors.muted, textAlign: 'center', padding: 40 }}>Loading\u2026</p>}
                    {!loadingSaved && selectedPeriod && savedRows.length === 0 && (
                        <p style={{ color: colors.muted, textAlign: 'center', padding: 40 }}>No KPI records found for this period.</p>
                    )}
                    {!loadingSaved && savedRows.length > 0 && (
                        <>
                            <SummaryBar rows={isEditing ? editingRows : savedRows} colors={colors} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <div style={{ color: colors.muted, fontSize: '13px' }}>
                                    {savedRows.length} consignments <FontAwesomeIcon icon={faMinus} style={{ width: 10, margin: '0 2px', opacity: 0.5 }} /> {fmtPeriod(selectedPeriod)}
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {canEdit && !isEditing && (
                                        <button style={btn('secondary')} onClick={handleStartEdit}>Edit</button>
                                    )}
                                    {canEdit && isEditing && (<>
                                        <button style={btn('secondary')} onClick={handleCancelEdit} disabled={savingEdits}>Cancel</button>
                                        <button style={btn('success')} onClick={handleSaveEdits} disabled={savingEdits}>
                                            {savingEdits ? 'Saving\u2026' : '\ud83d\udcbe Save Changes'}
                                        </button>
                                    </>)}
                                </div>
                            </div>
                            <KpiTable
                                rows={isEditing ? editingRows : savedRows}
                                editable={isEditing}
                                isDark={isDark} colors={colors}
                                onCommentChange={updateEditComment}
                                onNpcChange={updateEditNpc}
                            />
                        </>
                    )}
                </>
            )}

            {/* ── Empty / prompt state ─────────────────────────────────────── */}
            {mode === 'generate' && kpiRows === null && !generating && (
                <p style={{ color: colors.muted, textAlign: 'center', padding: 40 }}>Select a customer and date range, then click Generate.</p>
            )}
        </div>
    );
}
